import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { corporateTheme } from '../themes/corporate';
// Parallax functionality removed for compatibility

const HeroContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const HeroContent = styled.div`
  z-index: 10;
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(3rem, 8vw, 6rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  line-height: 1.1;
  text-align: center;
  width: 100%;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.h2)`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: ${corporateTheme.fontWeights.normal};
  color: ${corporateTheme.textSecondary};
  margin-bottom: 2rem;
  text-align: center;
  width: 100%;
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const Description = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.3rem);
  color: ${corporateTheme.textSecondary};
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    margin: 0 auto 2rem;
    max-width: 100%;
  }
`;

const CTAButton = styled(motion.button)`
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  border: none;
  padding: 1rem 3rem;
  font-size: 1.2rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${corporateTheme.cardShadow};
  margin-bottom: 3rem;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 40px rgba(37, 99, 235, 0.4);
    background: linear-gradient(135deg, ${corporateTheme.primaryLight} 0%, ${corporateTheme.primary} 100%);
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 1rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.8rem;
  }
  
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;
  background: ${corporateTheme.cardBackground};
  border-radius: ${corporateTheme.borderRadius.lg};
  backdrop-filter: blur(10px);
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: ${corporateTheme.borderRadius.md};
  }
  
  @media (max-width: 480px) {
    padding: 0.8rem;
    border-radius: ${corporateTheme.borderRadius.sm};
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.primary};
  margin-bottom: 0.5rem;
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 0.2rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textSecondary};
  font-weight: ${corporateTheme.fontWeights.medium};
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <HeroContainer>
        <HeroContent>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Title variants={itemVariants}>
              GrowTech
            </Title>
            
            <Subtitle variants={itemVariants}>
              Revolutionizing Affiliate Marketing in Eastern Asia
            </Subtitle>
            
            <Description variants={itemVariants}>
              Professional affiliate marketing platform targeting high-growth Eastern Asian markets 
              with €12M investment, proprietary content websites, and data-driven strategies.
            </Description>
            
            <CTAButton
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Explore Our Vision
            </CTAButton>
            
            <StatsContainer variants={itemVariants}>
              <StatItem
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <StatNumber>€12M</StatNumber>
                <StatLabel>Total Investment</StatLabel>
              </StatItem>
              
              <StatItem
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <StatNumber>8+</StatNumber>
                <StatLabel>Target Markets</StatLabel>
              </StatItem>
              
              <StatItem
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <StatNumber>€5M</StatNumber>
                <StatLabel>Content Investment</StatLabel>
              </StatItem>
              
              <StatItem
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <StatNumber>6</StatNumber>
                <StatLabel>Months to Launch</StatLabel>
              </StatItem>
            </StatsContainer>
          </motion.div>
        </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection; 